import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import type { FuseSeverity } from "../../lib/fuse-palette";
import { riskFlowEventKpis } from "../../lib/riskflow-event-kpis";
import { NothingFuse } from "../shared/NothingFuse";
import { useSettings } from "../../contexts/SettingsContext";

interface RiskFlowEventFuseGridProps {
  alert: Pick<
    RiskFlowAlert,
    | "subScores"
    | "direction"
    | "ivScore"
    | "headlineWeight"
    | "headlineIvScore"
    | "headlineEventWeight"
    | "headlineImpactWeight"
    | "headlineFreshness"
    | "vixAtPrint"
  >;
  severity: FuseSeverity;
  limit?: number;
  animateIn?: boolean;
  className?: string;
}

export function RiskFlowEventFuseGrid({
  alert,
  severity,
  limit = 4,
  animateIn = true,
  className = "",
}: RiskFlowEventFuseGridProps) {
  const { preferences } = useSettings();
  const kpis = riskFlowEventKpis(
    alert,
    limit,
    preferences.riskflowFilters?.showDirectionalScoring ?? true,
  );
  if (kpis.length === 0) return null;

  return (
    <div
      className={`riskflow-event-fuse-grid flex min-w-0 flex-col justify-between gap-3 rounded-[6px] border border-[var(--fintheon-accent)]/10 bg-[var(--fintheon-bg)]/35 p-3.5 ${className}`}
    >
      {kpis.map((kpi) => {
        const isPrimary = kpi.label === "EW";
        const isFuse = kpi.kind === "fuse" && kpi.score != null;
        const fuseScore = isFuse ? kpi.score : null;
        return (
          <div
            key={kpi.label}
            className={isFuse ? "min-w-0" : "min-w-0 py-0.5"}
          >
            <div
              className={`flex min-w-0 items-baseline justify-between gap-3 font-mono leading-[1.25] tracking-[0.04em] tabular-nums ${
                isPrimary ? "text-[13px]" : "text-[12px]"
              }`}
            >
              <span
                className={`truncate ${
                  isPrimary
                    ? "font-semibold text-[var(--fintheon-text)]/68"
                    : "text-[var(--fintheon-text)]/50"
                }`}
              >
                {fullKpiLabel(kpi.label)}
              </span>
              <span
                className={`shrink-0 ${
                  isPrimary
                    ? "text-[var(--fintheon-accent)]/90"
                    : "text-[var(--fintheon-accent)]/82"
                }`}
              >
                {kpi.value}
              </span>
            </div>
            {fuseScore != null && (
              <div className="mt-1">
                <NothingFuse
                  value={fuseScore / 10}
                  severity={severity}
                  orientation="horizontal"
                  thickness={5}
                  segments={5}
                  animateIn={animateIn}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function fullKpiLabel(label: string): string {
  if (label === "EW") return "Event Weight";
  if (label === "NS") return "Narrative Signal";
  if (label === "Bias") return "Bias";
  if (label === "VIX_PRINT") return "VIX at time of print";
  if (label === "VIX") return "VIX Multiplier";
  if (label === "Impact") return "Impact Weight";
  if (label === "Fresh") return "Freshness";
  return label;
}
